import type {
  PublicShareManifestResponse,
} from "@ultradrive/shared-types";

// Tiny fetch wrapper for the public-share endpoints. Unlike the
// dashboard there's no Auth0 token to attach — the share's own URL
// token is the auth (passed in the path), and password-unlock state
// rides on a path-scoped HttpOnly cookie. `credentials: "include"` is
// the only thing standing between a wrong-cookie request and a
// password gate.

interface ApiError {
  code: string;
  message: string;
}

interface ApiEnvelope<T> {
  success: boolean;
  data?: T;
  error?: ApiError;
}

async function call<T>(
  path: string,
  init?: RequestInit & { json?: unknown },
): Promise<T> {
  const headers = new Headers(init?.headers);
  let body: BodyInit | undefined = init?.body as BodyInit | undefined;
  if (init?.json !== undefined) {
    headers.set("Content-Type", "application/json");
    body = JSON.stringify(init.json);
  }
  const res = await fetch(path, {
    method: init?.method ?? (init?.json !== undefined ? "POST" : "GET"),
    credentials: "include",
    headers,
    body,
  });
  // Some routes (zip stream) are non-JSON. Callers that want bytes use
  // the absolute URL directly — this helper is JSON-only.
  let envelope: ApiEnvelope<T> | null = null;
  try {
    envelope = (await res.json()) as ApiEnvelope<T>;
  } catch {
    // fallthrough — unexpected non-JSON
  }
  if (!res.ok || !envelope?.success) {
    const code = envelope?.error?.code ?? `HTTP_${res.status}`;
    const message = envelope?.error?.message ?? `Request failed (${res.status})`;
    const err = new Error(message) as Error & { code: string; status: number };
    err.code = code;
    err.status = res.status;
    throw err;
  }
  return envelope.data as T;
}

export function getManifest(token: string): Promise<PublicShareManifestResponse> {
  return call<PublicShareManifestResponse>(
    `/v1/public-shares/${token}/manifest`,
  );
}

export function unlock(token: string, password: string): Promise<{ ok: true }> {
  return call<{ ok: true }>(`/v1/public-shares/${token}/unlock`, {
    json: { password },
  });
}

export function getPreviewUrl(
  token: string,
  fileId: string,
): Promise<{
  url: string | null;
  expiresAt: string | null;
  contentType: string;
  isProxy: boolean;
  proxyKind: "image" | "video" | null;
  error?: string;
}> {
  return call(`/v1/public-shares/${token}/items/${fileId}/preview-url`);
}

export function getDownloadUrl(
  token: string,
  fileId: string,
  opts?: { original?: boolean },
): Promise<{ url: string; expiresAt: string; isCold: boolean; isProxy: boolean }> {
  const q = opts?.original === false ? "?original=false" : "";
  return call(
    `/v1/public-shares/${token}/items/${fileId}/download-url${q}`,
  );
}

export function bulkZipUrl(token: string): string {
  return `/v1/public-shares/${token}/zip`;
}

// ── Contributions (anonymous uploads to a collab collection) ──
export interface ContributionInitResponse {
  sessionId: string;
  uploadUrl: string;
  expiresAt: string;
  contributorToken: string;
  isNewContributor: boolean;
}

export function initContribution(
  token: string,
  body: { name: string; size: number; mimeType: string; contributorName?: string | null },
): Promise<ContributionInitResponse> {
  return call(`/v1/public-shares/${token}/contributions/init`, { json: body });
}

export interface ContributionFinalizeResponse {
  fileId: string;
  name: string;
  pendingReview: boolean;
}

export function finalizeContribution(
  token: string,
  sessionId: string,
): Promise<ContributionFinalizeResponse> {
  return call(
    `/v1/public-shares/${token}/contributions/${sessionId}/finalize`,
    { json: {} },
  );
}

// Direct browser→R2 PUT, used between init and finalize. Returns the
// real bytes uploaded (R2 echoes Content-Length via the request).
export async function uploadToR2(
  uploadUrl: string,
  file: File,
): Promise<void> {
  const r = await fetch(uploadUrl, {
    method: "PUT",
    headers: { "Content-Type": file.type || "application/octet-stream" },
    body: file,
  });
  if (!r.ok) {
    const text = await r.text().catch(() => "");
    throw new Error(`R2 PUT failed: ${r.status} ${text.slice(0, 200)}`);
  }
}

// ── Multipart upload (resumable) ─────────────────────────
// Files larger than ~16 MB use this path so a broken-network retry
// only re-uploads the failed parts, not the whole file.

export interface InitMultipartResponse {
  sessionId: string;
  uploadId: string;
  partSize: number;
  partCount: number;
  partUrls: Array<{ partNumber: number; url: string }>;
  expiresAt: string;
  contributorToken: string;
  isNewContributor: boolean;
}

export function initMultipart(
  token: string,
  body: { name: string; size: number; mimeType: string; contributorName?: string | null },
): Promise<InitMultipartResponse> {
  return call(`/v1/public-shares/${token}/contributions/init-multipart`, {
    json: body,
  });
}

export function getPartUrl(
  token: string,
  sessionId: string,
  partNumber: number,
): Promise<{ url: string; expiresAt: string }> {
  return call(
    `/v1/public-shares/${token}/contributions/${sessionId}/part-url`,
    { json: { partNumber } },
  );
}

export function completeMultipart(
  token: string,
  sessionId: string,
  parts: Array<{ partNumber: number; etag: string }>,
): Promise<ContributionFinalizeResponse> {
  return call(
    `/v1/public-shares/${token}/contributions/${sessionId}/complete-multipart`,
    { json: { parts } },
  );
}

// PUT a single part. Reads the ETag from the response header (R2
// returns it on success). Retry once on failure with a fresh
// presigned URL — `refresh` fetches a new one for the same part.
export async function uploadPart(
  url: string,
  chunk: Blob,
  contentType: string,
  refresh?: () => Promise<string>,
): Promise<string> {
  const attempt = async (u: string): Promise<Response> =>
    fetch(u, {
      method: "PUT",
      headers: { "Content-Type": contentType },
      body: chunk,
    });
  let resp = await attempt(url);
  if (!resp.ok && refresh) {
    // Presigned URL might have expired or transient hiccup — get a
    // fresh one and try once more.
    const fresh = await refresh();
    resp = await attempt(fresh);
  }
  if (!resp.ok) {
    const text = await resp.text().catch(() => "");
    throw new Error(`Part PUT failed: ${resp.status} ${text.slice(0, 200)}`);
  }
  const etag = resp.headers.get("etag") ?? resp.headers.get("ETag");
  if (!etag) {
    throw new Error("Part PUT succeeded but no ETag header was returned");
  }
  return etag.replace(/"/g, "");
}
